// How scoring works, and how waivers work.
//
// Every number here is taken from backend/scoring.js
// (calculateTeamFantasyPoints and the multiplier block) and the rule constants
// in src/utils/season.js. If either changes, this changes with it: a rules page
// that disagrees with the scorer is worse than no rules page.
//
// Written for someone who has never seen a point spread, and deliberately free
// of gambling framing: no bookmakers, no betting, no "cover", "the line",
// "ATS" or "chalk". The audience is people who want to play a season-long game
// with friends. The expected margin is presented as a yardstick the app
// publishes, which is what it is from a manager's point of view, and where the
// number originates is not something they need to carry.
//
// The tiers are stated as the values that can actually occur, not as the raw
// intervals in the scoring.js ladder. Spreads come on the half point and
// margins are whole numbers, so the difference between them is always a
// multiple of 0.5. Writing the bands as open intervals to cover unreachable
// values like 7.2 made the positive and negative rows look like they had
// different boundaries, which is the one thing this table must not do.

import React from "react";
import { X } from "lucide-react";
import {
  SUPER_DOG_SPREAD,
  FIRST_CAPTAIN_WEEK,
  FIRST_TRIP_PLAY_WEEK,
  LAST_TRIP_PLAY_WEEK,
  FIRST_FREE_AGENCY_WEEK,
  LAST_BID_WEEK,
  LAST_FREE_AGENCY_WEEK,
  REGULAR_SEASON_WEEKS,
  WAIVER_BUDGET_MAX,
  WAIVER_BUDGET_STEP,
  WAIVER_MAX_CLAIMS,
  waiverBudgetForPlace,
} from "../utils/season";
import { useLeague } from "../context/LeagueContext";

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

// How far a team finished from its expected margin, positive for better than
// expected and negative for worse.
//
// The bands are symmetric, and stated as the values that can actually occur.
// Spreads are published on the half point and margins are whole numbers, so
// the difference is always a multiple of 0.5: there is no such thing as
// finishing 7.2 better, and describing the tiers as open intervals to cover
// values that cannot happen made the two directions read as though they had
// different boundaries when they do not.
const SPREAD_TIERS = [
  { range: "20 or more better", points: 5 },
  { range: "14.5 to 19.5 better", points: 3 },
  { range: "7.5 to 14 better", points: 2 },
  { range: "Half a point to 7 better", points: 1 },
  {
    range: "Exactly the expected margin",
    detail: "Only possible when the number is whole, such as a team expected to win by 7 winning by 7",
    points: 0,
  },
  { range: "Half a point to 7 worse", points: -1 },
  { range: "7.5 to 14 worse", points: -2 },
  { range: "14.5 to 19.5 worse", points: -3 },
  { range: "20 or more worse", points: -5 },
];

// Worked examples, in the order the sections introduce the ideas. Every total
// here is asserted against calculateTeamFantasyPoints in
// backend/test/scoring.test.js, so an example cannot quietly stop being true.
//
// Real matchups with real numbers, because the tier table alone never answers
// the question a new manager actually has: what is this measured against?
const EXAMPLES = [
  {
    team: "Ohio State",
    line: "-10",
    lineNote: "expected to win by 10",
    score: "Ohio State 31, Iowa 17",
    outcome: "Won by 14, which is 4 better than expected.",
    parts: ["Win +5", "Margin +1"],
    total: 6,
  },
  {
    team: "Iowa",
    line: "+17",
    lineNote: "expected to lose by 17",
    score: "Ohio State 24, Iowa 21",
    outcome: "Lost by 3, which is 14 better than expected. A Super Dog, so the loss costs nothing.",
    parts: ["Loss 0", "Margin +2"],
    total: 2,
  },
  {
    team: "Michigan",
    line: "-7",
    lineNote: "expected to win by 7",
    score: "Michigan 28, Purdue 21",
    outcome: "Won by exactly 7, landing on the number.",
    parts: ["Win +5", "Margin 0"],
    total: 5,
  },
  {
    team: "Alabama",
    line: "-21",
    lineNote: "expected to win by 21",
    score: "Vanderbilt 30, Alabama 20",
    outcome: "Lost by 10, which is 31 worse than expected. The worst a team can score.",
    parts: ["Loss -3", "Margin -5"],
    total: -8,
  },
  {
    team: "Vanderbilt",
    line: "+21",
    lineNote: "expected to lose by 21",
    score: "Vanderbilt 30, Alabama 20",
    outcome: "Won by 10, which is 31 better than expected. The best a team can score.",
    parts: ["Win +5", "Underdog +3", "Margin +5"],
    total: 13,
  },
];

function PointsPill({ points, note }) {
  const positive = points > 0;
  const zero = points === 0;
  const background = zero ? "var(--color-bg-page)" : positive ? "var(--color-success-bg)" : "var(--color-danger-bg)";
  const color = zero ? "var(--color-text-muted)" : positive ? "var(--color-success-text)" : "var(--color-danger-text)";
  return (
    <span
      className="flex-shrink-0 text-xs font-bold rounded-md px-2 py-1 text-center"
      style={{ minWidth: 44, backgroundColor: background, color }}
    >
      {note || (points > 0 ? `+${points}` : points)}
    </span>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="mb-5">
      <h3 className="text-sm font-bold uppercase tracking-wide mb-1" style={{ color: "var(--color-text-strong)" }}>
        {title}
      </h3>
      {subtitle && (
        <p className="text-xs mb-2" style={{ color: "var(--color-text-muted)" }}>
          {subtitle}
        </p>
      )}
      <div
        className="rounded-xl border overflow-hidden"
        style={{ backgroundColor: "var(--color-bg-card)", borderColor: "var(--color-border)" }}
      >
        {children}
      </div>
    </section>
  );
}

// A number inside a sentence. The result section states its points inline
// rather than in a column of pills, so the bonus reads as part of the step it
// belongs to instead of as a result of its own.
function Points({ children, negative }) {
  return (
    <strong style={{ color: negative ? "var(--color-danger-text)" : "var(--color-success-text)" }}>{children}</strong>
  );
}

// One outcome, as a heading with the steps that apply to it in order.
function Outcome({ title, steps, striped }) {
  return (
    <div
      className="px-3 py-3"
      style={{ backgroundColor: striped ? "var(--color-bg-row-alt)" : "var(--color-bg-card)" }}
    >
      <div className="text-sm font-semibold mb-1.5" style={{ color: "var(--color-text-strong)" }}>
        {title}
      </div>
      <ol className="list-decimal pl-5 space-y-1 text-xs leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
        {steps.map((step, i) => (
          <li key={i}>{step}</li>
        ))}
      </ol>
    </div>
  );
}

function Row({ label, detail, points, note, striped }) {
  return (
    <div
      className="flex items-center gap-3 px-3 py-2.5"
      style={{ backgroundColor: striped ? "var(--color-bg-row-alt)" : "var(--color-bg-card)" }}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm" style={{ color: "var(--color-text-strong)" }}>{label}</div>
        {detail && (
          <div className="text-xs mt-0.5" style={{ color: "var(--color-text-muted)" }}>{detail}</div>
        )}
      </div>
      {points !== undefined && <PointsPill points={points} note={note} />}
    </div>
  );
}

const ScoringSystemModal = ({ onClose }) => {
  // The budget ladder for this league's size, when the modal opens inside a
  // league. Optional chaining: nothing guarantees a LeagueContext above it.
  const leagueSize = useLeague()?.members?.length || 0;
  const budgetLadder = leagueSize > 1
    ? Array.from({ length: leagueSize }, (_, i) => leagueSize - i)
        .map((place) => ({ place, budget: waiverBudgetForPlace(place, leagueSize) }))
    : [];

  return (
    <div
      className="fixed inset-0 bg-black/40 z-[60] flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
    >
      {/* Full height on a phone, a centred card on a desktop. The old modal was
          max-w-4xl, which on a 390px screen was a wall of nested cards. */}
      <div
        className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl flex flex-col sheet-max-h"
        style={{ backgroundColor: "var(--color-bg-page)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-4 py-3.5 border-b flex-shrink-0 rounded-t-3xl sm:rounded-t-2xl"
          style={{ backgroundColor: "var(--color-bg-card)", borderColor: "var(--color-border)" }}
        >
          <h2 className="text-base font-bold" style={{ color: "var(--color-text-strong)" }}>
            Scoring and rules
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex items-center justify-center rounded-full"
            style={{ width: 32, height: 32, backgroundColor: "var(--color-bg-page)" }}
          >
            <X size={18} style={{ color: "var(--color-text-muted)" }} />
          </button>
        </div>

        {/* flex-1 min-h-0 is what makes this scroll. A flex child defaults to
            min-height:auto, so without min-h-0 it refuses to shrink below its
            content, overflows the sheet and takes the last section off screen
            with no way to reach it. The bottom padding clears the iOS home
            indicator, which otherwise sits over the final rows. */}
        <div
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pt-4"
          style={{ paddingBottom: "calc(4.5rem + env(safe-area-inset-bottom))" }}
        >
          <div
            className="rounded-xl border px-3 py-3 mb-5"
            style={{ backgroundColor: "var(--color-bg-card)", borderColor: "var(--color-border)" }}
          >
            <h3 className="text-sm font-bold mb-1.5" style={{ color: "var(--color-text-strong)" }}>
              First, the expected margin
            </h3>
            <p className="text-xs leading-relaxed mb-2" style={{ color: "var(--color-text-muted)" }}>
              Lineup publishes an expected margin for every game: how many points
              the stronger team is expected to win by. If Ohio State are expected
              to beat Iowa by 10, Ohio State are the favourite and Iowa are the
              underdog.
            </p>
            <p className="text-xs leading-relaxed mb-2" style={{ color: "var(--color-text-muted)" }}>
              It is a yardstick, so that beating a strong team counts for more
              than beating a weak one, and so a team that was never expected to
              win is not punished for losing narrowly.
            </p>
            <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
              Your team scores for the result, then again for how far it finished
              from that expected margin.
            </p>
          </div>

          <Section
            title="The result"
            subtitle="Counts for the five teams you start. Bench teams score nothing."
          >
            {/* Won and lost, each as a short numbered list rather than a row
                with a points pill.

                The pill layout put every number in a column down the right,
                which made the underdog bonus and the Super Dog look like two
                more results a team could get. Numbered inside the outcome they
                belong to, they read as what they are: a second step that only
                applies to that outcome. */}
            <Outcome
              title="Your team wins"
              steps={[
                <>If your team wins: <Points>+5</Points></>,
                <>If your team is the underdog: add an additional{" "}
                  <Points>+3 bonus</Points> (<Points>+8 total</Points>)</>,
              ]}
            />
            <Outcome
              title="Your team loses"
              striped
              steps={[
                <>If your team loses: <Points negative>-3</Points></>,
                <>If your team is a <Points>+{SUPER_DOG_SPREAD} or greater underdog</Points>,
                  there is no loss penalty applied</>,
              ]}
            />
            <div className="px-3 py-2.5" style={{ borderTop: "1px solid var(--color-bg-page)" }}>
              <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                After the above calculation, add or subtract your margin score,
                explained below.
              </p>
            </div>
          </Section>

          <Section
            title="The margin"
            subtitle="How far your team finished from its expected margin. Added to the result above, whether it won or lost."
          >
            {SPREAD_TIERS.map((tier, i) => (
              <Row
                key={tier.range}
                label={tier.range}
                detail={tier.detail}
                points={tier.points}
                striped={i % 2 === 1}
              />
            ))}
          </Section>

          <Section title="Multipliers">
            <Row
              label="Captain"
              detail={`One starter every week from week ${FIRST_CAPTAIN_WEEK}. If you do not pick one, the biggest favourite in your lineup is chosen for you.`}
              note="2x"
              points={2}
            />
            <Row
              label="5x Play"
              detail={`One per season, weeks ${FIRST_TRIP_PLAY_WEEK} to ${LAST_TRIP_PLAY_WEEK}. It sits on your captain, so it always resolves to 5x. Not available in the playoffs.`}
              note="5x"
              points={5}
              striped
            />
          </Section>

          <Section title="Freezes">
            <Row
              label="Three per season"
              detail="Lock a team's score once its game reaches the second half. The score you freeze is the score you keep, however the game ends. A freeze cannot be undone."
            />
          </Section>

          {/* Waivers sit here as well as on the rules page because this is the
              rules button managers actually press: the Lineup tab, beside
              their claims. Every number comes from season.js. */}
          <Section
            title="Waivers and free agency"
            subtitle={`After weeks ${FIRST_FREE_AGENCY_WEEK} to ${LAST_BID_WEEK}. Weeks 1 to ${FIRST_FREE_AGENCY_WEEK} and from week ${REGULAR_SEASON_WEEKS} on are played with the roster you have.`}
          >
            <Row label="Sunday 10am ET" detail="The week begins and bidding opens." />
            <Row label="Tuesday 4am ET" detail="Bidding closes." striped />
            <Row
              label="Tuesday 6am ET"
              detail="Every bid in the league resolves together. Free agency then opens: add any unowned team instantly until its game locks, 15 minutes before kickoff."
            />
          </Section>

          <Section title="Bidding">
            <Row
              label="Sealed bids"
              detail="Nobody sees your bid until it resolves. Claims are tried by rank: every manager's rank 1 claim first, then every rank 2, and so on, so a rival's higher-ranked claim that wins a team beats your lower-ranked one whatever the amounts. Between claims of the same rank, the highest bid wins. If two bids are equal, the manager lower in the standings wins."
            />
            <Row
              label="Your budget"
              detail={`Set by your place in the standings. Last place gets $${WAIVER_BUDGET_MAX}, and each place higher gets $${WAIVER_BUDGET_STEP} less. It resets every week: unspent money does not carry over, and winning a bid does not reduce next week's budget.`}
              striped
            />
            {budgetLadder.length > 0 && (
              <div className="px-3 pb-2.5" style={{ backgroundColor: "var(--color-bg-row-alt)" }}>
                <div className="text-xs mb-1" style={{ color: "var(--color-text-muted)" }}>
                  In your {leagueSize}-team league
                </div>
                <div className="grid grid-cols-3 gap-x-3 gap-y-0.5 text-xs" style={{ color: "var(--color-text-muted)" }}>
                  {budgetLadder.map(({ place, budget }) => (
                    <div key={place} className="flex justify-between">
                      <span>{ordinal(place)}</span>
                      <strong style={{ fontVariantNumeric: "tabular-nums" }}>${budget}</strong>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Row
              label="More than one claim"
              detail={`Your claims are tried in the order you rank them, not by amount. Your budget caps each claim, not the total: you can place up to ${WAIVER_MAX_CLAIMS} claims at your full budget, and you only pay for claims that win. Each win comes off your budget, and any claim below it that costs more than what is left is skipped. Bid your full budget on every claim and you get at most one team, the highest-ranked claim that wins. A $0 bid is allowed.`}
            />
            <Row
              label="Dropping a team"
              detail="Choose a team to drop, or none if you have an empty roster slot. Once your empty slots are used, any lower claim without a drop is skipped."
              striped
            />
            <Row
              label="Changing your mind"
              detail="Bidding on the same team again replaces your bid. Cancel a claim from Waivers on the Lineup tab."
            />
            <Row
              label="The season"
              detail={`The first window follows week ${FIRST_FREE_AGENCY_WEEK} and the last follows week ${LAST_BID_WEEK}, so week ${LAST_FREE_AGENCY_WEEK} is the last week a roster changes.`}
              striped
            />
          </Section>

          <Section
            title="Worked examples"
            subtitle="Real shapes of result. Totals are before any captain or 5x Play."
          >
            {EXAMPLES.map((example, i) => (
              <div
                key={`${example.team}-${example.line}`}
                className="px-3 py-2.5"
                style={{ backgroundColor: i % 2 === 1 ? "var(--color-bg-row-alt)" : "var(--color-bg-card)" }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold" style={{ color: "var(--color-text-strong)" }}>
                    {example.team}
                  </span>
                  <span
                    className="text-xs font-bold rounded px-1.5 py-0.5"
                    style={{ backgroundColor: "var(--color-bg-page)", color: "var(--color-text-muted)" }}
                  >
                    {example.line}
                  </span>
                  <span className="text-xs truncate" style={{ color: "var(--color-text-muted)" }}>
                    {example.lineNote}
                  </span>
                  <span className="flex-1" />
                  <PointsPill points={example.total} />
                </div>

                <div className="text-xs mt-1.5" style={{ color: "var(--color-text-strong)" }}>
                  {example.score}
                </div>
                <div className="text-xs mt-0.5 leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
                  {example.outcome}
                </div>

                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {example.parts.map((part) => (
                    <span
                      key={part}
                      className="text-xs rounded px-1.5 py-0.5"
                      style={{ backgroundColor: "var(--color-bg-page)", color: "var(--color-text-muted)" }}
                    >
                      {part}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </Section>

          <div
            className="rounded-xl border px-3 py-3"
            style={{ backgroundColor: "var(--color-bg-card)", borderColor: "var(--color-border)" }}
          >
            <h3 className="text-sm font-bold mb-1.5" style={{ color: "var(--color-text-strong)" }}>
              Worth knowing
            </h3>
            <ul className="space-y-1.5 text-xs" style={{ color: "var(--color-text-muted)" }}>
              <li>
                Before multipliers, one team scores between <strong>-8 and +13</strong>. The
                worst case is a favourite losing badly. A Super Dog cannot go below -5, because
                the loss itself costs it nothing.
              </li>
              <li>
                The captain doubles a bad week as well as a good one, so a captain who loses
                heavily costs you twice.
              </li>
              <li>
                An empty starting slot scores zero. There is no penalty for leaving one, and
                starting a team on a bye week does the same thing.
              </li>
              <li>
                Expected margins move during the week as injuries and conditions change the
                picture. The one used is whatever is on the game when it is scored, not the one
                showing when you set your lineup.
              </li>
              <li>
                Your lineup locks 15 minutes before each team's kickoff, team by team, so a
                Thursday game locks on Thursday while the rest of your lineup stays open. If a
                kickoff time has not been confirmed yet, that team locks from the start of its
                gameday.
              </li>
              <li>
                Week 0, the opening weekend in late August, awards no points. Those results still
                count toward a team's record: if Ohio State win on August 29 they go to 1-0, and
                it counts toward their record against the expected margin too, but you score
                nothing for it. Only 8 teams play that weekend, so paying it would reward the
                schedule rather than the manager.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoringSystemModal;
